import { CoursePageFooter } from '@/components/course/CoursePageFooter';
import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader';
import { PromoCtaBanner } from '@/components/courses/PromoCtaBanner';
import { SubscriptionHubCtas } from '@/components/subscription/SubscriptionHubCtas';
import { HelpScoutBeacon } from '@/components/HelpScoutBeacon';
import { LessonCard } from '@/components/lesson/LessonCard';
import {
  Container,
  CourseThumbnailsSection,
  ErrorDisplay,
  StaticSection,
  useErrorHandler,
} from '@/components/ui';
import { useHideLessonFromContinue } from '@/hooks/useHideLessonFromContinue';
import { usePurchasedCoursesPageData } from '@/hooks/usePurchasedCoursesPageData';
import { useTrialCourseCta } from '@/hooks/useTrialCourseCta';
import { useAuth } from '@/lib/AuthContext';
import { CourseStatus, CourseType, URLS } from '@/lib/constants';
import { getCourseBySlug } from '@/supabase/services/courses';
import { useLessonCardLayout } from '@/lib/useDeviceType';
import { welcomeBackTitle } from '@/lib/userDisplayName';
import type { Lesson } from '@/types';
import { buildLessonUrl } from '@/utils/buildLessonUrl';
import { useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { Linking, Platform, RefreshControl } from 'react-native';
import { ScrollView, YStack } from 'tamagui';
import { BodyText, PageTitle } from '@/components/ui/Typography';

export default function PurchasedCoursesScreen() {
  const { user } = useAuth();
  const trialCta = useTrialCourseCta(user?.id);
  const router = useRouter();
  const lessonCardLayout = useLessonCardLayout();
  const [refreshing, setRefreshing] = useState(false);
  const { showError, showSuccess } = useErrorHandler();

  const {
    purchasedCourses,
    continueLessons,
    isLoading,
    error,
    refetch,
  } = usePurchasedCoursesPageData(user?.id);

  const welcomeTitle = welcomeBackTitle(user?.user_metadata);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const handleContinuePress = useCallback(
    (lesson: Lesson) => {
      const courseSlug = lesson.course_slug;
      if (!courseSlug) return;
      router.push(
        buildLessonUrl({
          courseSlug,
          lessonSlug: lesson.slug,
        }) as any,
      );
    },
    [router],
  );

  const handleSeeAllPurchased = useCallback(() => {
    router.push('/purchased-courses/all');
  }, [router]);

  const hideLessonFromContinue = useHideLessonFromContinue(user?.id);
  const handleContinueRemove = useCallback(
    (lesson: Lesson) => {
      hideLessonFromContinue.mutate(lesson.id, {
        onSuccess: () => showSuccess('Removed from Continue watching'),
        onError: (err) =>
          showError(
            err instanceof Error ? err.message : 'Failed to remove lesson',
          ),
      });
    },
    [hideLessonFromContinue, showSuccess, showError],
  );

  const statusBySlug = React.useMemo(
    () => new Map(purchasedCourses.map((c) => [c.slug, c.status])),
    [purchasedCourses],
  );

  // Per-lesson (companion) grants: the card deep-links to the granted lesson.
  const lessonSlugBySlug = React.useMemo(
    () =>
      new Map(
        purchasedCourses
          .filter((c) => c.lessonSlug)
          .map((c) => [c.slug, c.lessonSlug as string]),
      ),
    [purchasedCourses],
  );

  const handlePurchasedCoursePress = useCallback(
    async (slug: string) => {
      if (statusBySlug.get(slug) !== CourseStatus.Published) {
        // Redirect-type courses (e.g. The Harmony Games) have a canonical
        // home inside Josephine — follow it instead of the WordPress fallback.
        try {
          const course = await getCourseBySlug(slug);
          if (course?.type === CourseType.Redirect && course.redirect_url) {
            router.push(course.redirect_url as any);
            return;
          }
        } catch {
          // course lookup failed — use the WordPress fallback below
        }
        const url = URLS.course(slug);
        if (Platform.OS === 'web') {
          window.location.href = url;
        } else {
          void Linking.openURL(url);
        }
        return;
      }
      const lessonSlug = lessonSlugBySlug.get(slug);
      if (lessonSlug) {
        router.push(
          buildLessonUrl({ courseSlug: slug, lessonSlug }) as any,
        );
        return;
      }
      router.push({
        pathname: '/courses/[course-slug]',
        params: { 'course-slug': slug },
      });
    },
    [statusBySlug, lessonSlugBySlug, router],
  );

  if (isLoading) {
    return (
      <YStack f={1} bg="$bg">
        <HelpScoutBeacon />
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingBottom: Platform.OS === 'web' ? 0 : 16,
          }}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
        >
          <CoursePagePremiumLoader variant="course" />
        </ScrollView>
      </YStack>
    );
  }

  if (error) {
    return (
      <YStack f={1} bg="#F8F9FA">
        <ErrorDisplay error={error} onRetry={onRefresh} />
      </YStack>
    );
  }

  return (
    <YStack f={1} bg="$bg">
      <HelpScoutBeacon />
      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{
          flexGrow: 1,
          ...(Platform.OS === 'web' ? { minHeight: '100%' } : {}),
          paddingBottom: Platform.OS === 'web' ? 0 : 16,
        }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <Container py="$5">
          {/* An enabled First Steps trial CTA replaces the promo banner for its audience. */}
          {!trialCta.active ? <PromoCtaBanner userId={user?.id} /> : null}
          <SubscriptionHubCtas userId={user?.id} />
          <PageTitle tag="h2" fontWeight="700" color="$color">
            {welcomeTitle}
          </PageTitle>
        </Container>

        {purchasedCourses.length > 0 ? (
          <CourseThumbnailsSection
            title="Purchased Courses"
            data={purchasedCourses}
            onPressCourse={handlePurchasedCoursePress}
            onSeeAll={handleSeeAllPurchased}
          />
        ) : (
          <Container py="$3">
            <YStack
              bg="#F4F1ED"
              borderRadius="$4"
              px="$4"
              py="$3"
              maw={1140}
              w="100%"
              mx="auto"
            >
              <BodyText color="$color" textAlign="center">
                You have not yet purchased courses with lifetime access
              </BodyText>
            </YStack>
          </Container>
        )}

        {continueLessons.length > 0 ? (
          <YStack w="100%" maw={1140} mx="auto">
            <StaticSection
              title="Continue"
              data={continueLessons}
              renderItem={({ item }) => (
                <LessonCard
                  item={item}
                  onPress={() => handleContinuePress(item)}
                  onRemove={user ? () => handleContinueRemove(item) : undefined}
                  {...lessonCardLayout}
                />
              )}
            />
          </YStack>
        ) : null}

        {Platform.OS === 'web' ? (
          <YStack flexGrow={1} minHeight={1} width="100%" />
        ) : null}
        <CoursePageFooter />
      </ScrollView>
    </YStack>
  );
}
